import { Store } from 'lucide-react'
import { useCallback, useEffect, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { connectionsApi, needsApi, ratingsApi, trustApi } from '../api/endpoints'
import { useAuth } from '../auth/AuthContext'
import { NeedsSection } from '../components/NeedsSection'
import { AboutSection } from '../components/profile/AboutSection'
import { AddressSection } from '../components/profile/AddressSection'
import { GuardianLinks } from '../components/profile/GuardianLinks'
import { InviteCard } from '../components/profile/InviteCard'
import { RecentRatings } from '../components/profile/RecentRatings'
import { NearbyRecommendations } from '../components/profile/NearbyRecommendations'
import { RegistrationDetails } from '../components/profile/RegistrationDetails'
import { TutorStats } from '../components/profile/TutorStats'
import { InstitutionVerificationStatus } from '../components/profile/InstitutionVerificationStatus'
import { VerifiedBadge } from '../components/VerifiedBadge'
import { VerificationGateBanner } from '../components/profile/VerificationGateBanner'
import { VerificationPhotos } from '../components/profile/VerificationPhotos'
import { requiresGuardian } from '../utils/age'
import type {
  ConnectionRequestResponse,
  LearnerNeedResponse,
  RatingResponse,
  TrustStatusResponse,
} from '../types/api'

/** The provider dashboard heading, named for what they are rather than "tutor" for all. */
const DASHBOARD_TITLE: Partial<Record<string, string>> = {
  TUTOR: 'Your tutor dashboard',
  CENTRE: 'Your centre dashboard',
  COUNSELLOR: 'Your counsellor dashboard',
  SCHOOL: 'Your school dashboard',
  COLLEGE: 'Your college dashboard',
  COMPANY: 'Your company dashboard',
}

/** entityKind is still STUDENT in the database and the API - only the badge reads "Learner" now, matching the role-select card. */
const ENTITY_KIND_LABEL: Partial<Record<string, string>> = {
  STUDENT: 'Learner',
}


export function ProfilePage() {
  const { profile, refreshProfile } = useAuth()
  const location = useLocation()
  const notice = (location.state as { notice?: string } | null)?.notice ?? null

  const [needs, setNeeds] = useState<LearnerNeedResponse[]>([])
  // Not the same as needs being empty: until the first answer, or if it fails, we do not know.
  const [needsState, setNeedsState] = useState<'loading' | 'ready' | 'failed'>('loading')
  const [ratings, setRatings] = useState<RatingResponse[]>([])
  const [connections, setConnections] = useState<ConnectionRequestResponse[]>([])
  const [trustStatus, setTrustStatus] = useState<TrustStatusResponse | null>(null)

  const isSupply = profile?.persona === 'SUPPLY'
  const isInstitution =
    profile?.entityKind === 'SCHOOL' ||
    profile?.entityKind === 'COLLEGE' ||
    profile?.entityKind === 'COMPANY'
  const isDemand = profile?.persona === 'DEMAND'

  const loadNeeds = useCallback(() => {
    needsApi
      .listOwn()
      .then((list) => {
        setNeeds(list)
        setNeedsState('ready')
      })
      .catch(() => setNeedsState('failed'))
  }, [])
  const loadSupplyDashboard = useCallback((participantId: string) => {
    ratingsApi.listForParticipant(participantId).then(setRatings).catch(() => null)
    connectionsApi.listOwn().then(setConnections).catch(() => null)
    // Carries the reviewing admin's note, which is the actionable half of a rejection.
    trustApi.getStatus().then(setTrustStatus).catch(() => null)
  }, [])

  useEffect(() => {
    if (!profile) return
    if (isDemand) loadNeeds()
    if (isSupply) loadSupplyDashboard(profile.id)
  }, [profile, isDemand, isSupply, loadNeeds, loadSupplyDashboard])

  // A plain client-side navigation (the SetupReminderBanner's "Add what you want to learn" link,
  // chiefly) does not get the browser's own scroll-to-#hash behaviour - that only fires on a full
  // page load. Waits on needsState rather than firing once on mount, because NeedsSection does not
  // exist in the DOM yet at that point - it is gated on isDemand and needs still being 'loading'.
  useEffect(() => {
    if (!location.hash || needsState === 'loading') return
    const target = document.getElementById(location.hash.slice(1))
    target?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }, [location.hash, needsState])

  if (!profile) return <div className="text-ink-500">Loading profile...</div>

  const isGuardian = profile.entityKind === 'GUARDIAN'

  const averageRating = ratings.length
    ? ratings.reduce((sum, r) => sum + r.rating, 0) / ratings.length
    : null
  const acceptedConnections = connections.filter((c) => c.status === 'ACCEPTED').length
  // An enquiry is a request someone else sent me that I have not answered.
  const enquiries = connections.filter(
    (c) => c.status === 'PENDING' && c.receiverParticipantId === profile.id,
  ).length

  return (
    <>
      {notice && (
        <div className="mb-6 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-800">
          {notice}
        </div>
      )}

      <VerificationGateBanner profile={profile} status={trustStatus} />

      {isSupply && (
        <div className="mb-6">
          <h1 className="flex items-center gap-2 text-xl font-bold text-ink-900">
            <Store size={18} className="text-sun-500" /> {DASHBOARD_TITLE[profile.entityKind] ?? 'Your dashboard'}
          </h1>
          <p className="text-sm text-ink-500">How your listing is performing and what learners are saying.</p>
          <div className="mt-4">
            <TutorStats
              connections={acceptedConnections}
              enquiries={enquiries}
              averageRating={averageRating}
              ratingCount={ratings.length}
            />
          </div>
        </div>
      )}

      <div className="grid gap-6 lg:grid-cols-3">
        <div className="space-y-6 lg:col-span-2">
          <div className="card-pad bg-gradient-to-br from-sun-100 to-white">
            <div className="flex items-center gap-3">
              <span className="grid h-14 w-14 shrink-0 place-items-center rounded-2xl bg-white text-2xl font-bold text-amber-600 shadow-sm">
                {profile.fullName.charAt(0).toUpperCase()}
              </span>
              <div className="min-w-0">
                <h1 className="break-words text-2xl font-bold text-ink-900">{profile.fullName}</h1>
                <div className="mt-1 flex flex-wrap gap-1.5">
                  <span className="pill bg-ink-100 text-ink-700">
                    {ENTITY_KIND_LABEL[profile.entityKind] ?? titleCase(profile.entityKind)}
                  </span>
                  {isSupply && <VerifiedBadge status={profile.verificationStatus} />}
                  {profile.gradeBand && <span className="pill bg-sun-400/20 text-amber-800">{profile.gradeBand}</span>}
                  {profile.gender && <span className="pill bg-ink-100 text-ink-700">{formatGender(profile.gender)}</span>}
                  <span className={`pill ${consentToneClass(profile.consentStatus)}`}>
                    Consent {profile.consentStatus.replace('_', ' ').toLowerCase()}
                  </span>
                  {requiresGuardian(profile) && <span className="pill bg-red-100 text-red-700">Minor</span>}
                </div>
              </div>
            </div>
          </div>

          {/* Guardian linking is a demand-side concern; a tutor has no parent to approve them. A
              guardian account has no parent of its own to link either - their children live on
              /guardian, in full, so this would only show their own child's name under a "Parent
              or guardian" heading that means the opposite of what it says. */}
          {isDemand && !isGuardian && (
            <GuardianLinks participantId={profile.id} guardianRequired={requiresGuardian(profile)} />
          )}

          {/* Providers no longer submit verification documents from their profile: the Yellow
              Dot team verifies them directly, and VerificationGateBanner says where they stand.
              Institutions keep their status card - their check is a premises visit against a
              public registry code, already queued when they registered. */}
          {isInstitution && (
            <InstitutionVerificationStatus profile={profile} note={trustStatus?.verificationNote} />
          )}

          {/* Tutors, schools and colleges: 3 to 5 photos for the team to verify them by. Renders
              nothing for anyone else - the server says who is asked. */}
          {isSupply && <VerificationPhotos verificationStatus={profile.verificationStatus} />}

          <RegistrationDetails profile={profile} />

          {/* Bio, qualifications, fee range, experience - a tutor's own words, never touched by
              NeedProjection, so unlike serviceType there is nothing derived here for an edit to
              fight with. Institutions describe themselves through their own registration fields
              instead (see InstitutionVerificationStatus), not this one. */}
          {isSupply && !isInstitution && <AboutSection profile={profile} onSaved={refreshProfile} />}

          {/* The complete address neither a student's nor a tutor's registration collects beyond
              a pincode - both forms stay to the essentials, and there was nowhere to add the rest
              afterwards until now. Institutions are the exception: their address already has its
              own registration step and its own InstitutionProfile row, so a second, generic editor
              here would just be a second place for the same fact to drift out of sync. */}
          {!isInstitution && <AddressSection profile={profile} onSaved={refreshProfile} />}

          {isDemand && <NeedsSection needs={needs} onChanged={loadNeeds} forGuardian={isGuardian} />}

          {isSupply && <RecentRatings ratings={ratings} />}
        </div>

        <div className="space-y-6">
          <InviteCard />
          {/* Every role gets one, and it is a different list for each - see NearbyRecommendations. */}
          <NearbyRecommendations
            audience={isDemand ? 'DEMAND' : 'SUPPLY'}
            roleKey={`${profile.id}:${profile.entityKind}`}
            needs={needs}
            needsState={needsState}
          />
        </div>
      </div>
    </>
  )
}

function titleCase(value: string): string {
  return value.charAt(0) + value.slice(1).toLowerCase()
}

function formatGender(gender: string): string {
  return gender.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase())
}

function consentToneClass(status: string): string {
  if (status === 'GRANTED') return 'bg-emerald-100 text-emerald-700'
  if (status === 'OPTED_OUT') return 'bg-red-100 text-red-700'
  return 'bg-ink-100 text-ink-700'
}
